import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { SydFilterContext } from "src/components/Common/UI/SydFilter/SydFilterContext";
import { format } from 'date-fns';
import { useTranslation } from "react-i18next";
import { convertApiDataToReactTableData, convertApiHeaderToReactTableHeader } from "src/components/Common/UI/DndTable/UtilFunctions";
import { useGetAllLines } from "../api/getAllLines";
import { useLineDownloadCsv } from "../api/getLineDownloadCsv";
import { useGetLineDownloadExcel } from "../api/getLineDownloadExcel";


// All your logic is here
export const useLinesDataViewLogic = () => {
  const { activeFilters } = useContext(SydFilterContext);
  const [modalEvent, setModalEvent] = useState<IEvent>({action: "",selectedRows: [],});
  const [selectedRowsOfTable, setSelectedRowsOfTable] = useState<IGetLines["data"]>([]);
  const getAllLinesQuery = useGetAllLines({data: activeFilters as IDataLineSearchForm});
  const { t } = useTranslation();
  const downloadAnchorRef = useRef<HTMLAnchorElement>(null);
  const lineDownloadCsvMutation = useLineDownloadCsv({data: activeFilters as IDataLineSearchForm});
  const lineDownloadExcelMutation = useGetLineDownloadExcel({data: activeFilters as IDataLineSearchForm});

  const loadingDone = !getAllLinesQuery.isLoading
  const errorData = [getAllLinesQuery].some(({ isError }) => isError);

  const dataTypes = [
    { id: 0, apiType: "Default", frontType: "", frontDisplayFormat: "" },
    { id: 1, apiType: "Integer", frontType: "number", frontDisplayFormat: "" },
    { id: 2, apiType: "Double", frontType: "number", frontDisplayFormat: "" },
    { id: 3, apiType: "Date", frontType: "date", frontDisplayFormat: "YYYY-MM-DD", sourceDisplayFormat: "YYYY-MM-DDTHH:mm:ss" },
    { id: 4, apiType: "String", frontType: "string", frontDisplayFormat: "" },
    { id: 5, apiType: "yyyyMMdd", frontType: "date", frontDisplayFormat: "YYYY-MM-DD", sourceDisplayFormat: "YYYYMMDD" },
    { id: 6, apiType: "Boolean", frontType: "boolean", frontDisplayFormat: "" },
    { id: 7, apiType: "IntegerTime", frontType: "date", frontDisplayFormat: "HH:mm:ss", sourceDisplayFormat: "seconds" },
    { id: 8, apiType: "yyyyMM", frontType: "date", frontDisplayFormat: "MMM YYYY", sourceDisplayFormat: "YYYYMM" }
  ];

  let canDownloadCsv = false;
  let canDownloadExcel = false;

  if(getAllLinesQuery.data){
    canDownloadCsv = getAllLinesQuery.data?.data.length > 0;
    canDownloadExcel = getAllLinesQuery.data?.data.length > 0;
  }

  const data = getAllLinesQuery.data ? getAllLinesQuery.data.data : [];
  const apiTotalRecords = getAllLinesQuery.data ? getAllLinesQuery.data.totalRercords : 0;
  const visibleTotalRecords = getAllLinesQuery.data ? getAllLinesQuery.data.data.length : 0;

  const numberOfRowsTitle = visibleTotalRecords < apiTotalRecords
    ? `Γραμμές Data. [Συνολικές εγγραφές: ${apiTotalRecords}]`
    : 'Γραμμές Data.';

  const columnData = [
    {
      columnName: "id",
      columnDisplayName: "Γραμμή",
      visible: true,
      columnOrder: 1,
      isDraggable: false,
      columnDataType: "String",
    },
    {
      columnName: "hcNmsNum",
      columnDisplayName: "ID Number",
      visible: true,
      columnOrder: 2,
      isDraggable: false,
      columnDataType: "String",
    },
    {
      columnName: "phoneNumber",
      columnDisplayName: "Αριθμός",
      visible: true,
      columnOrder: 3,
      isDraggable: false,
      columnDataType: "String",
    },
    {
      columnName: "provider",
      columnDisplayName: "Πάροχος",
      visible: true,
      columnOrder: 4,
      isDraggable: false,
      columnDataType: "String",
    },
    {
      columnName: "unit",
      columnDisplayName: "Μονάδα",
      visible: true,
      columnOrder: 5,
      isDraggable: false,
      columnDataType: "String",
    },
    {
      columnName: "descline",
      columnDisplayName: "Περιγραφή",
      visible: true,
      columnOrder: 6,
      isDraggable: false,
      columnDataType: "String",
    },
    {
      columnName: "cost",
      columnDisplayName: "Έλεγχος",
      visible: true,
      columnOrder: 7,
      isDraggable: false,
      columnDataType: "String",
    },
    {
      columnName: "comments",
      columnDisplayName: "Σχόλια",
      visible: true,
      columnOrder: 8,
      isDraggable: false,
      columnDataType: "String",
    },
    {
      columnName: "removalDate",
      columnDisplayName: "Ημερομηνία Διαγραφής",
      visible: true,
      columnOrder: 9,
      isDraggable: false,
      columnDataType: "Date",
    }

  ]

  const columns = useMemo(() => {
    console.log("columnData",columnData)
    return convertApiHeaderToReactTableHeader(
      columnData ,
      dataTypes
    );
  }, []);

  const closeActionModal = () => {
    setModalEvent({ action: "", selectedRows: [] });
  };

  const downloadFile = (blobString: string, reportName: string, fileExtension: string) => {
    if (downloadAnchorRef.current) {
      const recordingsBlob = new Blob([blobString], { type: 'application/octet-stream' }); // Set the type to 'application/octet-stream'
      const recordingsBlobURL = URL.createObjectURL(recordingsBlob);

      const currentDate: Date = new Date();
      const nowDateFormated: string = format(currentDate, 'yyyy-MM-dd_HH-mm-ss');
      const fileName:string = `${reportName}_${nowDateFormated}.${fileExtension}`;

      downloadAnchorRef.current.href = recordingsBlobURL;
      downloadAnchorRef.current.download = fileName;
      downloadAnchorRef.current.click();
    }
  }

  const DownloadCsv = async () => {
    lineDownloadCsvMutation.mutateAsync({data: activeFilters as IDataLineSearchForm})
    .then((apiRecordingsCsvResponse) => {
      downloadFile(apiRecordingsCsvResponse, "DataLine", "csv");
    }).catch((error) => {
      console.log(error)
    }) 
  }

  const DownloadExcel = async () => {
    lineDownloadExcelMutation.mutateAsync({data: activeFilters as IDataLineSearchForm})
    .then((apiRecordingsExcelResponse) => {
      downloadFile(apiRecordingsExcelResponse, "DataLine", "xlsx");
    }).catch((error) => {
      console.log(error)
    })
  }

  const getModalTitleByAction = (): string => {
    let title = "";
    switch (modalEvent.action) {
      case "create":
        title = t("Δημιουργία Γραμμής Data");
        break;
      case "edit":
        title = t("Επεξεργασία Γραμμής Data");
        break;
      case "delete":
        title = t("Διαγραφή Γραμμής Data");
        break;
      default:
        break;
    }
    return title;
  };

  const actionEvent = async (event: IEvent) => {
    const selectedRows: number[] = selectedRowsOfTable[0]
      ? [...[selectedRowsOfTable[0].id]]
      : [];
      switch (event.action) {
        case "downloadCsv":
          await DownloadCsv();
          break;
        case "downloadExcel":
          await DownloadExcel();
          break;
        default:
        setModalEvent({ action: event.action, selectedRows: selectedRows });
      }
  };

  useEffect(() => {
    setSelectedRowsOfTable([]);
    getAllLinesQuery.refetch();
  }, [activeFilters]);

  return {
    data,
    columns,
    loadingDone,
    selectedRowsOfTable,
    setSelectedRowsOfTable,
    t,
    modalEvent,
    actionEvent,
    closeActionModal,
    getModalTitleByAction,
    numberOfRowsTitle,
    visibleTotalRecords,
    downloadAnchorRef,
    canDownloadCsv,
    canDownloadExcel,
  };
};
